import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { DateTime } from 'luxon'
import { useSetting } from '../../hooks'
import {
    TCalendarPlan,
    TCalendarPlanRequest,
    useCalendarPlanPreview,
    useCancelCalendarPlan,
    useConfirmCalendarPlan,
} from '../../services/api/calendar-plans.hooks'
import { useIsFeatureEnabled } from '../../services/api/feature-flags.hooks'
import { useCalendarContext } from './CalendarContext'

interface CalendarPlanContextValue {
    plan?: TCalendarPlan
    request?: TCalendarPlanRequest
    isLoading: boolean
    isConfirming: boolean
    isClearing: boolean
    isReviewing: boolean
    isDismissed: boolean
    hasPendingConfirmation: boolean
    confirmationError?: string
    openReview: () => void
    dismiss: () => void
    clear: () => void
    reblock: () => void
    confirm: () => void
}

const CalendarPlanContext = createContext<CalendarPlanContextValue | undefined>(undefined)

export const CalendarPlanProvider = ({ children }: { children: ReactNode }) => {
    const enabled = useIsFeatureEnabled('calendar_planning')
    const location = useLocation()
    const { field_value: accountID } = useSetting('calendar_account_id_for_new_tasks', enabled)
    const { field_value: calendarID } = useSetting('calendar_calendar_id_for_new_tasks', enabled)
    const { field_value: startTime } = useSetting('calendar_plan_start', enabled)
    const { field_value: endTime } = useSetting('calendar_plan_end', enabled)
    const { field_value: morning } = useSetting('calendar_plan_morning', enabled)
    const [isReviewing, setIsReviewing] = useState(false)
    const [isDismissed, setIsDismissed] = useState(false)
    const [manualRequested, setManualRequested] = useState(false)
    const [today, setToday] = useState(() => DateTime.local().toISODate())
    const [pendingConfirmation, setPendingConfirmation] = useState<{
        request: TCalendarPlanRequest
        fingerprint: string
        plan: TCalendarPlan
    }>()
    const [confirmationError, setConfirmationError] = useState<string>()
    const { setCalendarType, setDate, setDayViewDate, setIsCollapsed } = useCalendarContext()

    useEffect(() => {
        let timer: number
        const scheduleRollover = () => {
            const now = DateTime.local()
            timer = window.setTimeout(() => {
                setToday(DateTime.local().toISODate())
                setIsDismissed(false)
                setManualRequested(false)
                scheduleRollover()
            }, now.plus({ days: 1 }).startOf('day').diff(now).as('milliseconds') + 1000)
        }
        scheduleRollover()
        return () => window.clearTimeout(timer)
    }, [])

    const request = useMemo<TCalendarPlanRequest | undefined>(() => {
        if (!enabled || !accountID || !calendarID || !startTime || !endTime) return undefined
        const now = DateTime.local()
        return {
            date: today,
            timezone: now.zoneName,
            start_time: startTime,
            end_time: endTime,
            account_id: accountID,
            calendar_id: calendarID,
        }
    }, [accountID, calendarID, enabled, endTime, startTime, today])
    const onOverview = location.pathname === '/' || location.pathname.startsWith('/overview')
    const shouldPrepare = manualRequested || isReviewing || (morning === 'true' && onOverview)
    const preview = useCalendarPlanPreview(request, enabled && shouldPrepare)
    const confirmMutation = useConfirmCalendarPlan()
    const cancelMutation = useCancelCalendarPlan()
    const recoveredConfirmation =
        preview.data?.pending && preview.data.request
            ? { request: preview.data.request, fingerprint: preview.data.fingerprint, plan: preview.data }
            : undefined
    const activeConfirmation = pendingConfirmation ?? recoveredConfirmation
    const displayedPlan = activeConfirmation?.plan ?? preview.data
    const displayedRequest = activeConfirmation?.request ?? request

    const value = useMemo<CalendarPlanContextValue>(
        () => ({
            plan: displayedPlan,
            request: displayedRequest,
            isLoading: preview.isLoading,
            isConfirming: confirmMutation.isLoading,
            isClearing: cancelMutation.isLoading,
            isReviewing,
            isDismissed,
            hasPendingConfirmation: Boolean(activeConfirmation),
            confirmationError,
            openReview: () => {
                const today = DateTime.local()
                setManualRequested(true)
                setCalendarType('day')
                setDate(today)
                setDayViewDate(today)
                setIsCollapsed(false)
                setIsReviewing(true)
            },
            dismiss: () => setIsDismissed(true),
            clear: () => {
                setConfirmationError(undefined)
                if (!activeConfirmation) {
                    setIsReviewing(false)
                    return
                }
                cancelMutation.mutate(
                    { fingerprint: activeConfirmation.fingerprint },
                    {
                        onSuccess: () => {
                            setPendingConfirmation(undefined)
                            setIsReviewing(false)
                        },
                        onError: () => setConfirmationError('Could not clear this plan. Try again.'),
                    }
                )
            },
            reblock: () => {
                if (!activeConfirmation) {
                    setManualRequested(true)
                    preview.refetch()
                }
            },
            confirm: () => {
                const confirmation =
                    activeConfirmation ??
                    (request && preview.data
                        ? { request, fingerprint: preview.data.fingerprint, plan: preview.data }
                        : undefined)
                if (!confirmation) return
                setConfirmationError(undefined)
                setPendingConfirmation(confirmation)
                confirmMutation.mutate(
                    { request: confirmation.request, fingerprint: confirmation.fingerprint },
                    {
                        onSuccess: () => {
                            setPendingConfirmation(undefined)
                            setIsReviewing(false)
                        },
                        onError: (error) => {
                            const status = (error as { response?: { status?: number } }).response?.status
                            if (status === 409) {
                                setConfirmationError('Calendar changed. Clear and re-block.')
                                if (!confirmation.plan.pending) {
                                    setPendingConfirmation(undefined)
                                    preview.refetch()
                                }
                            } else {
                                const detail = (error as { response?: { data?: { detail?: string } } }).response?.data
                                    ?.detail
                                setConfirmationError(
                                    detail ?? 'Some blocks could not be added. Try again or clear the plan.'
                                )
                            }
                        },
                    }
                )
            },
        }),
        [
            confirmMutation.isLoading,
            cancelMutation,
            activeConfirmation,
            confirmationError,
            displayedPlan,
            displayedRequest,
            isDismissed,
            isReviewing,
            preview.data,
            preview.isLoading,
            request,
        ]
    )

    return <CalendarPlanContext.Provider value={value}>{children}</CalendarPlanContext.Provider>
}

export const useCalendarPlan = () => {
    const value = useOptionalCalendarPlan()
    if (!value) throw new Error('useCalendarPlan must be used inside CalendarPlanProvider')
    return value
}

export const useOptionalCalendarPlan = () => useContext(CalendarPlanContext)
